// HostelManagement.js - Hostel Management (Boys & Girls)
// Tabs: Requests | New Request | Rooms | Register Student |
//       Complaints | Fee | Mess Menu | Staff
// Pure UI — no backend/API calls. All data lives in local React state (with
// a few demo entries pre-loaded so the page isn't empty on first look).
// COLORS: #0D47A1, #2196F3, #90CAF9, #E3F2FD (brand) + semantic status colors
import { useState, useMemo } from "react";
import { getSchoolName } from "./schoolNameStore";
import { Building2, Footprints, Home, Check, X, AlertTriangle, Trash2, Plus, Pin, BedDouble, HardHat, Calendar, Phone, ArrowLeftRight, Circle, PenLine, Zap, Lightbulb, Droplet, Armchair, Brush, Wifi, Wrench, Wind, Feather, Package, ClipboardList, CheckCircle2 } from "lucide-react";
const NAVY = "#0D47A1";
const BLUE = "#2196F3";
const LIGHT = "#90CAF9";
const PALE = "#E3F2FD";
const STATUS_COLOR = {
  Pending: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FB8C00"
  },
  Approved: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#43A047"
  },
  Rejected: {
    bg: "#FFEBEE",
    text: "#C62828",
    dot: "#E53935"
  }
};
const TYPE_COLOR = {
  Leave: {
    bg: NAVY,
    label: "Leave"
  },
  Permission: {
    bg: BLUE,
    label: "Permission"
  }
};
const COMPLAINT_STATUS_COLOR = {
  Pending: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FB8C00"
  },
  "In Progress": {
    bg: "#E3F2FD",
    text: "#1565C0",
    dot: "#2196F3"
  },
  Resolved: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#43A047"
  }
};
const CATEGORY_ICON = {
  Electrical: <Lightbulb className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Plumbing: <Droplet className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Furniture: <Armchair className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Cleanliness: <Brush className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Internet: <Wifi className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  Other: <Wrench className="inline w-[1em] h-[1em] align-[-0.125em]" />
};
const FEE_STATUS_COLOR = {
  Paid: {
    bg: "#E8F5E9",
    text: "#2E7D32",
    dot: "#43A047"
  },
  Pending: {
    bg: "#FFF3E0",
    text: "#E65100",
    dot: "#FB8C00"
  },
  Overdue: {
    bg: "#FFEBEE",
    text: "#C62828",
    dot: "#E53935"
  }
};
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MEALS = [{
  key: "breakfast",
  label: "Breakfast"
}, {
  key: "lunch",
  label: " Lunch"
}, {
  key: "dinner",
  label: "Dinner"
}];

// Room furniture / inventory items tracked per room
const INVENTORY_ITEMS = [{
  key: "fan",
  label: "Fan",
  icon: <Wind className="inline w-[1em] h-[1em] align-[-0.125em]" />
}, {
  key: "kattil",
  label: "Cot",
  icon: ""
}, {
  key: "pillow",
  label: "Pillow",
  icon: <Feather className="inline w-[1em] h-[1em] align-[-0.125em]" />
}, {
  key: "bench",
  label: "Bench",
  icon: ""
}, {
  key: "chair",
  label: "Chair",
  icon: <Armchair className="inline w-[1em] h-[1em] align-[-0.125em]" />
}, {
  key: "cupboard",
  label: "Cupboard",
  icon: <Package className="inline w-[1em] h-[1em] align-[-0.125em]" />
}];
function blankInventory() {
  return Object.fromEntries(INVENTORY_ITEMS.map(i => [i.key, 0]));
}
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return d;
  }
}
function fmtMoney(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}
function newId(prefix) {
  return prefix + "_" + Date.now() + "_" + Math.floor(Math.random() * 1000);
}

// ── Demo seed data ───────────────────────────────────────────────────────
const DEMO_ROOMS = [{
  id: "room_b101",
  hostel: "Boys",
  roomNo: "101",
  floor: "1st Floor",
  capacity: 4,
  notes: "Near warden's office"
}, {
  id: "room_b102",
  hostel: "Boys",
  roomNo: "102",
  floor: "1st Floor",
  capacity: 4,
  notes: "Standard room"
}, {
  id: "room_b103",
  hostel: "Boys",
  roomNo: "103",
  floor: "2nd Floor",
  capacity: 3,
  notes: "Near washroom"
}, {
  id: "room_g201",
  hostel: "Girls",
  roomNo: "201",
  floor: "1st Floor",
  capacity: 4,
  notes: "Near warden's office"
}, {
  id: "room_g202",
  hostel: "Girls",
  roomNo: "202",
  floor: "2nd Floor",
  capacity: 3,
  notes: "Standard room"
}];
const DEMO_STUDENTS = [{
  id: "st1",
  name: "Arun Kumar",
  address: "12, Gandhi St, Salem",
  parentNumber: "9876500011",
  altNumber: "9876511011",
  schoolName: getSchoolName() + " Group of Schools",
  className: "9",
  section: "A",
  hostel: "Boys",
  gender: "Male",
  roomId: "room_b101"
}, {
  id: "st2",
  name: "Vignesh S",
  address: "45, Anna Nagar, Salem",
  parentNumber: "9876500022",
  altNumber: "",
  schoolName: getSchoolName() + " Group of Schools",
  className: "10",
  section: "B",
  hostel: "Boys",
  gender: "Male",
  roomId: "room_b102"
}, {
  id: "st3",
  name: "Karthik R",
  address: "8, Bazaar St, Attur",
  parentNumber: "9876500033",
  altNumber: "9876533033",
  schoolName: getSchoolName() + " Group of Schools",
  className: "8",
  section: "A",
  hostel: "Boys",
  gender: "Male",
  roomId: "room_b101"
}, {
  id: "st4",
  name: "Priya Dharshini",
  address: "22, North St, Salem",
  parentNumber: "9876500044",
  altNumber: "",
  schoolName: getSchoolName() + " Group of Schools",
  className: "9",
  section: "A",
  hostel: "Girls",
  gender: "Female",
  roomId: "room_g201"
}, {
  id: "st5",
  name: "Divya Sri",
  address: "3, Temple St, Attur",
  parentNumber: "9876500055",
  altNumber: "9876555055",
  schoolName: getSchoolName() + " Group of Schools",
  className: "11",
  section: "B",
  hostel: "Girls",
  gender: "Female",
  roomId: "room_g201"
}, {
  id: "st6",
  name: "Meena Loshini",
  address: "17, Main Rd, Salem",
  parentNumber: "9876500066",
  altNumber: "",
  schoolName: getSchoolName() + " Group of Schools",
  className: "7",
  section: "A",
  hostel: "Girls",
  gender: "Female",
  roomId: "room_g202"
}];
const DEMO_REQUESTS = [{
  id: "r1",
  studentId: "st1",
  studentName: "Arun Kumar",
  className: "9",
  section: "A",
  hostel: "Boys",
  parentName: "Parent",
  parentMobile: "9876500011",
  type: "Leave",
  fromDate: todayStr(),
  toDate: todayStr(),
  reason: "Family function at home",
  status: "Pending",
  wardenRemarks: "",
  returned: false,
  createdAt: new Date().toISOString()
}, {
  id: "r2",
  studentId: "st3",
  studentName: "Karthik R",
  className: "8",
  section: "A",
  hostel: "Boys",
  parentName: "Parent",
  parentMobile: "9876500033",
  type: "Permission",
  date: todayStr(),
  outTime: "16:00",
  expectedInTime: "18:30",
  destination: "Clinic",
  reason: "Dental checkup appointment",
  status: "Approved",
  wardenRemarks: "Approved, be back by 6:30",
  returned: false,
  createdAt: new Date().toISOString()
}, {
  id: "r3",
  studentId: "st4",
  studentName: "Priya Dharshini",
  className: "9",
  section: "A",
  hostel: "Girls",
  parentName: "Parent",
  parentMobile: "9876500044",
  type: "Leave",
  fromDate: todayStr(),
  toDate: todayStr(),
  reason: "Not feeling well, going home to rest",
  status: "Approved",
  wardenRemarks: "",
  returned: true,
  createdAt: new Date().toISOString()
}, {
  id: "r4",
  studentId: "st5",
  studentName: "Divya Sri",
  className: "11",
  section: "B",
  hostel: "Girls",
  parentName: "Parent",
  parentMobile: "9876500055",
  type: "Permission",
  date: todayStr(),
  outTime: "10:00",
  expectedInTime: "12:00",
  destination: "Bank (KYC work)",
  reason: "Need to submit KYC documents in person",
  status: "Pending",
  wardenRemarks: "",
  returned: false,
  createdAt: new Date().toISOString()
}];
const DEFAULT_RULES = ["Lights-off & silence by 9:30 PM on weekdays", "ID card must be worn / carried at all times inside campus", "No outside food or guests allowed inside the room", "Visiting hours: Saturday & Sunday, 4:00 PM – 6:00 PM only", "Any Leave / Permission must be approved by the Warden before leaving", "Room must be kept clean; random inspections may happen"];
const DEMO_COMPLAINTS = [{
  id: "c1",
  studentId: "st2",
  category: "Electrical",
  description: "Ceiling fan making noise and running slow",
  status: "Pending",
  assignedTo: "",
  createdAt: new Date().toISOString()
}, {
  id: "c2",
  studentId: "st3",
  category: "Plumbing",
  description: "Tap in the common washroom is leaking continuously",
  status: "In Progress",
  assignedTo: "Ramesh (Plumber)",
  createdAt: new Date().toISOString()
}, {
  id: "c3",
  studentId: "st5",
  category: "Furniture",
  description: "One bed frame leg is broken, needs replacement",
  status: "Resolved",
  assignedTo: "Maintenance Team",
  createdAt: new Date().toISOString()
}, {
  id: "c4",
  studentId: "st6",
  category: "Internet",
  description: "WiFi very weak on the 2nd floor corridor",
  status: "Pending",
  assignedTo: "",
  createdAt: new Date().toISOString()
}];
const FEE_TERMS = ["Term 1 (Jun - Sep)", "Term 2 (Oct - Jan)", "Term 3 (Feb - May)"];
const DEMO_FEES = [{
  id: "f1",
  studentId: "st1",
  term: "Term 1 (Jun - Sep)",
  amount: 15000,
  dueDate: "2026-07-15",
  status: "Paid",
  paidDate: "2026-07-10"
}, {
  id: "f2",
  studentId: "st2",
  term: "Term 1 (Jun - Sep)",
  amount: 15000,
  dueDate: "2026-07-15",
  status: "Pending",
  paidDate: ""
}, {
  id: "f3",
  studentId: "st3",
  term: "Term 1 (Jun - Sep)",
  amount: 15000,
  dueDate: "2026-06-30",
  status: "Pending",
  paidDate: ""
}, {
  id: "f4",
  studentId: "st4",
  term: "Term 1 (Jun - Sep)",
  amount: 15000,
  dueDate: "2026-07-15",
  status: "Paid",
  paidDate: "2026-07-08"
}, {
  id: "f5",
  studentId: "st5",
  term: "Term 1 (Jun - Sep)",
  amount: 15000,
  dueDate: "2026-07-15",
  status: "Paid",
  paidDate: "2026-07-12"
}, {
  id: "f6",
  studentId: "st6",
  term: "Term 1 (Jun - Sep)",
  amount: 15000,
  dueDate: "2026-06-30",
  status: "Pending",
  paidDate: ""
}];
const DEMO_MESS_MENU = {
  Monday: {
    breakfast: "Idli, Sambar, Chutney",
    lunch: "Rice, Sambar, Poriyal, Curd",
    dinner: "Chapati, Kurma"
  },
  Tuesday: {
    breakfast: "Pongal, Vadai, Chutney",
    lunch: "Rice, Rasam, Kootu, Appalam",
    dinner: "Idiyappam, Kurma"
  },
  Wednesday: {
    breakfast: "Dosa, Sambar, Chutney",
    lunch: "Rice, Sambar, Poriyal, Curd",
    dinner: "Chapati, Channa Masala"
  },
  Thursday: {
    breakfast: "Bread, Jam, Boiled Egg / Milk",
    lunch: "Rice, Rasam, Kootu, Appalam",
    dinner: "Variety Rice, Raita"
  },
  Friday: {
    breakfast: "Poori, Aloo Masala",
    lunch: "Rice, Sambar, Poriyal, Curd",
    dinner: "Chapati, Paneer / Veg Kurma"
  },
  Saturday: {
    breakfast: "Rava Upma, Chutney",
    lunch: "Chicken / Veg Biryani, Raita",
    dinner: "Chapati, Dal"
  },
  Sunday: {
    breakfast: "Set Dosa, Sambar",
    lunch: "Rice, Sambar, Poriyal, Sweet",
    dinner: "Chapati, Kurma, Ice Cream"
  }
};
const DEMO_STAFF = [{
  id: "s1",
  name: "Mr. Selvam",
  role: "Warden",
  hostel: "Boys",
  contact: "9876000011"
}, {
  id: "s2",
  name: "Ravi",
  role: "Security",
  hostel: "Boys",
  contact: "9876000033"
}, {
  id: "s3",
  name: "Muthu",
  role: "Cook",
  hostel: "Boys",
  contact: "9876000055"
}, {
  id: "s4",
  name: "Mrs. Kavitha",
  role: "Warden",
  hostel: "Girls",
  contact: "9876000022"
}, {
  id: "s5",
  name: "Lakshmi",
  role: "Matron",
  hostel: "Girls",
  contact: "9876000044"
}, {
  id: "s6",
  name: "Selvi",
  role: "Cook",
  hostel: "Girls",
  contact: "9876000066"
}];
const DEMO_INVENTORY = {
  room_b101: {
    fan: 2,
    kattil: 4,
    pillow: 4,
    bench: 1,
    chair: 2,
    cupboard: 1
  },
  room_b102: {
    fan: 1,
    kattil: 4,
    pillow: 4,
    bench: 0,
    chair: 2,
    cupboard: 1
  },
  room_b103: {
    fan: 1,
    kattil: 3,
    pillow: 3,
    bench: 1,
    chair: 1,
    cupboard: 1
  },
  room_g201: {
    fan: 2,
    kattil: 4,
    pillow: 4,
    bench: 1,
    chair: 2,
    cupboard: 1
  },
  room_g202: {
    fan: 1,
    kattil: 3,
    pillow: 3,
    bench: 0,
    chair: 1,
    cupboard: 1
  }
};

// Principal <-> Warden messages, scoped per hostel
const DEMO_MESSAGES = [{
  id: "msg1",
  hostel: "Boys",
  from: "Principal",
  priority: "Normal",
  body: "Please ensure night roll call is completed by 9:30 PM daily and share the weekly compliance report every Sunday.",
  createdAt: new Date(Date.now() - 3 * 86400000).toISOString()
}, {
  id: "msg2",
  hostel: "Boys",
  from: "Warden",
  priority: "Normal",
  body: "Noted sir. Weekly report will be shared every Sunday evening without fail.",
  createdAt: new Date(Date.now() - 3 * 86400000 + 3600000).toISOString()
}, {
  id: "msg3",
  hostel: "Boys",
  from: "Principal",
  priority: "Urgent",
  body: "Room 103 fan complaint has been pending — please get it fixed today.",
  createdAt: new Date(Date.now() - 86400000).toISOString()
}, {
  id: "msg4",
  hostel: "Girls",
  from: "Principal",
  priority: "Normal",
  body: "Parents' visiting day is this Saturday. Please prepare the visitor register in advance.",
  createdAt: new Date(Date.now() - 2 * 86400000).toISOString()
}, {
  id: "msg5",
  hostel: "Girls",
  from: "Warden",
  priority: "Normal",
  body: "Register is ready sir. Also requesting an additional cot for Room 202, one student is joining next week.",
  createdAt: new Date(Date.now() - 2 * 86400000 + 5400000).toISOString()
}];
const emptyRequestForm = {
  type: "Leave",
  fromDate: "",
  toDate: "",
  date: "",
  outTime: "",
  expectedInTime: "",
  destination: "",
  reason: ""
};
const emptyRegForm = {
  name: "",
  address: "",
  parentNumber: "",
  altNumber: "",
  schoolName: getSchoolName() + " Group of Schools",
  className: "",
  section: "",
  roomId: ""
};
const emptyRoomForm = {
  roomNo: "",
  floor: "",
  capacity: "4",
  notes: ""
};
const emptyComplaintForm = {
  studentId: "",
  category: "Electrical",
  description: ""
};
const emptyFeeForm = {
  studentId: "",
  term: FEE_TERMS[0],
  amount: "15000",
  dueDate: ""
};
const emptyStaffForm = {
  name: "",
  role: "Warden",
  contact: ""
};
const emptyMessageForm = {
  priority: "Normal",
  body: ""
};
export default function HostelManagement() {
  const [hostel, setHostel] = useState("Boys"); // 'Boys' | 'Girls'
  const [activeTab, setActiveTab] = useState("requests");
  const [statusFilter, setStatusFilter] = useState("All");
  const [rooms, setRooms] = useState(DEMO_ROOMS);
  const [students, setStudents] = useState(DEMO_STUDENTS);
  const [requests, setRequests] = useState(DEMO_REQUESTS);
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [newRule, setNewRule] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [reqForm, setReqForm] = useState(emptyRequestForm);
  const [reqError, setReqError] = useState("");
  const [reqSuccess, setReqSuccess] = useState("");
  const [regForm, setRegForm] = useState(emptyRegForm);
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState("");
  const [roomForm, setRoomForm] = useState(emptyRoomForm);
  const [roomError, setRoomError] = useState("");

  // ── New module state ─────────────────────────────────────────────────
  const [complaints, setComplaints] = useState(DEMO_COMPLAINTS);
  const [complaintForm, setComplaintForm] = useState(emptyComplaintForm);
  const [complaintError, setComplaintError] = useState("");
  const [complaintFilter, setComplaintFilter] = useState("All");
  const [assignInputs, setAssignInputs] = useState({});
  const [fees, setFees] = useState(DEMO_FEES);
  const [feeForm, setFeeForm] = useState(emptyFeeForm);
  const [feeError, setFeeError] = useState("");
  const [feeFilter, setFeeFilter] = useState("All");
  const [messMenu, setMessMenu] = useState(DEMO_MESS_MENU);
  const [messDay, setMessDay] = useState(DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]);
  const [staff, setStaff] = useState(DEMO_STAFF);
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [staffError, setStaffError] = useState("");
  const [roomInventory, setRoomInventory] = useState(DEMO_INVENTORY);
  const [messages, setMessages] = useState(DEMO_MESSAGES);
  const [senderRole, setSenderRole] = useState("Principal"); // "Principal" | "Warden" — who is typing
  const [messageForm, setMessageForm] = useState(emptyMessageForm);
  const [messageError, setMessageError] = useState("");

  // ── Derived data ──────────────────────────────────────────────────────
  const hostelRooms = useMemo(() => rooms.filter(r => r.hostel === hostel), [rooms, hostel]);
  const hostelStudents = useMemo(() => students.filter(s => s.hostel === hostel), [students, hostel]);
  const hostelRequests = useMemo(() => requests.filter(r => r.hostel === hostel), [requests, hostel]);
  const hostelStaff = useMemo(() => staff.filter(s => s.hostel === hostel), [staff, hostel]);
  const studentIds = useMemo(() => new Set(hostelStudents.map(s => s.id)), [hostelStudents]);
  const hostelComplaints = useMemo(() => complaints.filter(c => studentIds.has(c.studentId)), [complaints, studentIds]);
  const hostelFees = useMemo(() => fees.filter(f => studentIds.has(f.studentId)), [fees, studentIds]);
  const studentById = id => students.find(s => s.id === id);
  const roomNoFor = student => rooms.find(r => r.id === student?.roomId)?.roomNo;
  const occupancyByRoom = useMemo(() => {
    const map = {};
    hostelStudents.forEach(s => {
      if (!s.roomId) return;
      map[s.roomId] = (map[s.roomId] || 0) + 1;
    });
    return map;
  }, [hostelStudents]);
  const roomLabel = r => {
    const occ = occupancyByRoom[r.id] || 0;
    return `Room ${r.roomNo} — ${occ}/${r.capacity} occupied${occ >= r.capacity ? " (FULL)" : ""}`;
  };
  const roomStats = useMemo(() => {
    const totalRooms = hostelRooms.length;
    const totalCapacity = hostelRooms.reduce((sum, r) => sum + Number(r.capacity || 0), 0);
    const totalOccupied = hostelStudents.filter(s => s.roomId).length;
    return {
      totalRooms,
      totalCapacity,
      totalOccupied,
      available: totalCapacity - totalOccupied
    };
  }, [hostelRooms, hostelStudents]);
  const hostelInventoryTotals = useMemo(() => {
    const totals = blankInventory();
    hostelRooms.forEach(r => {
      const inv = roomInventory[r.id] || {};
      INVENTORY_ITEMS.forEach(item => {
        totals[item.key] += Number(inv[item.key] || 0);
      });
    });
    return totals;
  }, [hostelRooms, roomInventory]);
  const hostelMessages = useMemo(() => messages.filter(m => m.hostel === hostel).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)), [messages, hostel]);
  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    return hostelStudents.filter(s => !q || s.name.toLowerCase().includes(q) || s.className.toLowerCase().includes(q) || (rooms.find(r => r.id === s.roomId)?.roomNo || "").toLowerCase().includes(q));
  }, [hostelStudents, studentSearch, rooms]);
  const filteredRequests = useMemo(() => {
    if (statusFilter === "All") return hostelRequests;
    return hostelRequests.filter(r => r.status === statusFilter);
  }, [hostelRequests, statusFilter]);
  const reqStats = useMemo(() => {
    const pending = hostelRequests.filter(r => r.status === "Pending").length;
    const approved = hostelRequests.filter(r => r.status === "Approved").length;
    const today = todayStr();
    const outNow = hostelRequests.filter(r => {
      if (r.status !== "Approved" || r.returned) return false;
      if (r.type === "Leave") return r.fromDate <= today && today <= r.toDate;
      if (r.type === "Permission") return r.date === today;
      return false;
    }).length;
    return {
      total: hostelRequests.length,
      pending,
      approved,
      outNow
    };
  }, [hostelRequests]);

  // Complaint stats & derived status (Pending fee marked Overdue automatically for display)
  const complaintStats = useMemo(() => {
    const pending = hostelComplaints.filter(c => c.status === "Pending").length;
    const inProgress = hostelComplaints.filter(c => c.status === "In Progress").length;
    const resolved = hostelComplaints.filter(c => c.status === "Resolved").length;
    return {
      total: hostelComplaints.length,
      pending,
      inProgress,
      resolved
    };
  }, [hostelComplaints]);
  const filteredComplaints = useMemo(() => {
    if (complaintFilter === "All") return hostelComplaints;
    return hostelComplaints.filter(c => c.status === complaintFilter);
  }, [hostelComplaints, complaintFilter]);
  const displayFees = useMemo(() => {
    const today = todayStr();
    return hostelFees.map(f => ({
      ...f,
      displayStatus: f.status === "Pending" && f.dueDate && f.dueDate < today ? "Overdue" : f.status
    }));
  }, [hostelFees]);
  const feeStats = useMemo(() => {
    const collected = displayFees.filter(f => f.status === "Paid").reduce((s, f) => s + Number(f.amount || 0), 0);
    const pendingAmt = displayFees.filter(f => f.status === "Pending").reduce((s, f) => s + Number(f.amount || 0), 0);
    const overdue = displayFees.filter(f => f.displayStatus === "Overdue").length;
    return {
      collected,
      pendingAmt,
      overdue,
      count: displayFees.length
    };
  }, [displayFees]);
  const filteredFees = useMemo(() => {
    if (feeFilter === "All") return displayFees;
    return displayFees.filter(f => f.displayStatus === feeFilter);
  }, [displayFees, feeFilter]);
  function switchHostel(h) {
    setHostel(h);
    setActiveTab("requests");
    resetRequestForm();
    setRegForm(emptyRegForm);
    setRegError("");
    setRegSuccess("");
    setComplaintForm(emptyComplaintForm);
    setComplaintError("");
    setFeeForm(emptyFeeForm);
    setFeeError("");
    setStaffForm(emptyStaffForm);
    setStaffError("");
  }

  // ── Requests logic ────────────────────────────────────────────────────
  function pickStudent(s) {
    setSelectedStudent(s);
    const roomNo = rooms.find(r => r.id === s.roomId)?.roomNo;
    setStudentSearch(`${s.name}${roomNo ? `  (Room ${roomNo})` : ""}`);
  }
  function resetRequestForm() {
    setReqForm(emptyRequestForm);
    setSelectedStudent(null);
    setStudentSearch("");
    setReqError("");
    setReqSuccess("");
  }
  function submitRequest() {
    setReqError("");
    setReqSuccess("");
    if (!selectedStudent) {
      setReqError("Please select a student from the search results.");
      return;
    }
    if (!reqForm.reason.trim()) {
      setReqError("Please enter a reason.");
      return;
    }
    if (reqForm.type === "Leave" && (!reqForm.fromDate || !reqForm.toDate)) {
      setReqError("Please fill both From Date and To Date.");
      return;
    }
    if (reqForm.type === "Leave" && reqForm.fromDate > reqForm.toDate) {
      setReqError("From Date cannot be after To Date.");
      return;
    }
    if (reqForm.type === "Permission" && (!reqForm.date || !reqForm.outTime)) {
      setReqError("Please fill Date and Out Time.");
      return;
    }
    const newRequest = {
      id: newId("r"),
      studentId: selectedStudent.id,
      studentName: selectedStudent.name,
      className: selectedStudent.className,
      section: selectedStudent.section,
      hostel,
      parentName: "Parent",
      parentMobile: selectedStudent.parentNumber,
      type: reqForm.type,
      reason: reqForm.reason,
      fromDate: reqForm.fromDate,
      toDate: reqForm.toDate,
      date: reqForm.date,
      outTime: reqForm.outTime,
      expectedInTime: reqForm.expectedInTime,
      destination: reqForm.destination,
      status: "Pending",
      wardenRemarks: "",
      returned: false,
      createdAt: new Date().toISOString()
    };
    setRequests(prev => [newRequest, ...prev]);
    setReqSuccess("Request submitted successfully.");
    resetRequestForm();
    setTimeout(() => setActiveTab("requests"), 700);
  }
  function decide(id, status) {
    setRequests(prev => prev.map(r => r.id === id ? {
      ...r,
      status
    } : r));
  }
  function toggleReturned(id, current) {
    setRequests(prev => prev.map(r => r.id === id ? {
      ...r,
      returned: !current
    } : r));
  }

  // ── Room management ───────────────────────────────────────────────────
  function addRoom() {
    setRoomError("");
    if (!roomForm.roomNo.trim()) {
      setRoomError("Room number is required.");
      return;
    }
    if (hostelRooms.some(r => r.roomNo === roomForm.roomNo.trim())) {
      setRoomError(`Room ${roomForm.roomNo} already exists in ${hostel} hostel.`);
      return;
    }
    const cap = Number(roomForm.capacity);
    if (!cap || cap < 1) {
      setRoomError("Capacity must be at least 1.");
      return;
    }
    const newRoomId = newId("room");
    setRooms(prev => [...prev, {
      id: newRoomId,
      hostel,
      roomNo: roomForm.roomNo.trim(),
      floor: roomForm.floor.trim() || "—",
      capacity: cap,
      notes: roomForm.notes.trim()
    }]);
    setRoomInventory(prev => ({
      ...prev,
      [newRoomId]: blankInventory()
    }));
    setRoomForm(emptyRoomForm);
  }
  function deleteRoom(id) {
    const occ = occupancyByRoom[id] || 0;
    if (occ > 0) {
      alert("Cannot delete a room that currently has students assigned. Reassign them first.");
      return;
    }
    setRooms(prev => prev.filter(r => r.id !== id));
    setRoomInventory(prev => {
      const copy = {
        ...prev
      };
      delete copy[id];
      return copy;
    });
  }

  // ── Inventory logic ───────────────────────────────────────────────────
  function updateInventoryCount(roomId, itemKey, value) {
    const num = Math.max(0, Math.floor(Number(value) || 0));
    setRoomInventory(prev => ({
      ...prev,
      [roomId]: {
        ...(prev[roomId] || blankInventory()),
        [itemKey]: num
      }
    }));
  }

  // ── Messaging logic (Principal <-> Warden) ──────────────────────────────
  function sendMessage() {
    setMessageError("");
    if (!messageForm.body.trim()) {
      setMessageError("Please type a message.");
      return;
    }
    const newMessage = {
      id: newId("msg"),
      hostel,
      from: senderRole,
      priority: messageForm.priority,
      body: messageForm.body.trim(),
      createdAt: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMessage]);
    setMessageForm(emptyMessageForm);
  }
  function addRule() {
    if (!newRule.trim()) return;
    setRules(prev => [...prev, newRule.trim()]);
    setNewRule("");
  }
  function removeRule(idx) {
    setRules(prev => prev.filter((_, i) => i !== idx));
  }

  // ── Student registration ──────────────────────────────────────────────
  function submitRegistration() {
    setRegError("");
    setRegSuccess("");
    if (!regForm.name.trim()) {
      setRegError("Student name is required.");
      return;
    }
    if (!regForm.address.trim()) {
      setRegError("Address is required.");
      return;
    }
    if (!regForm.parentNumber.trim() || regForm.parentNumber.trim().length < 10) {
      setRegError("Please enter a valid parent number.");
      return;
    }
    if (!regForm.className.trim()) {
      setRegError("Class is required.");
      return;
    }
    if (!regForm.section.trim()) {
      setRegError("Section is required.");
      return;
    }
    if (!regForm.roomId) {
      setRegError("Please select a room to allot.");
      return;
    }
    const room = rooms.find(r => r.id === regForm.roomId);
    const occ = occupancyByRoom[regForm.roomId] || 0;
    if (room && occ >= room.capacity) {
      setRegError(`Room ${room.roomNo} is already full.`);
      return;
    }
    const newStudent = {
      id: newId("st"),
      name: regForm.name.trim(),
      address: regForm.address.trim(),
      parentNumber: regForm.parentNumber.trim(),
      altNumber: regForm.altNumber.trim(),
      schoolName: regForm.schoolName.trim(),
      className: regForm.className.trim(),
      section: regForm.section.trim(),
      hostel,
      gender: hostel === "Boys" ? "Male" : "Female",
      roomId: regForm.roomId
    };
    setStudents(prev => [...prev, newStudent]);
    setRegSuccess(`${newStudent.name} registered and allotted Room ${room?.roomNo}.`);
    setRegForm(emptyRegForm);
  }

  // ── Complaints logic ──────────────────────────────────────────────────
  function submitComplaint() {
    setComplaintError("");
    if (!complaintForm.studentId) {
      setComplaintError("Please select a student.");
      return;
    }
    if (!complaintForm.description.trim()) {
      setComplaintError("Please describe the issue.");
      return;
    }
    const newComplaint = {
      id: newId("c"),
      studentId: complaintForm.studentId,
      category: complaintForm.category,
      description: complaintForm.description.trim(),
      status: "Pending",
      assignedTo: "",
      createdAt: new Date().toISOString()
    };
    setComplaints(prev => [newComplaint, ...prev]);
    setComplaintForm(emptyComplaintForm);
  }
  function updateComplaintStatus(id, status) {
    setComplaints(prev => prev.map(c => c.id === id ? {
      ...c,
      status
    } : c));
  }
  function assignStaffToComplaint(id) {
    const name = (assignInputs[id] || "").trim();
    if (!name) return;
    setComplaints(prev => prev.map(c => c.id === id ? {
      ...c,
      assignedTo: name,
      status: c.status === "Pending" ? "In Progress" : c.status
    } : c));
    setAssignInputs(prev => ({
      ...prev,
      [id]: ""
    }));
  }

  // ── Fee logic ──────────────────────────────────────────────────────────
  function addFeeRecord() {
    setFeeError("");
    if (!feeForm.studentId) {
      setFeeError("Please select a student.");
      return;
    }
    if (!feeForm.amount || Number(feeForm.amount) <= 0) {
      setFeeError("Please enter a valid amount.");
      return;
    }
    if (!feeForm.dueDate) {
      setFeeError("Please pick a due date.");
      return;
    }
    setFees(prev => [...prev, {
      id: newId("f"),
      studentId: feeForm.studentId,
      term: feeForm.term,
      amount: Number(feeForm.amount),
      dueDate: feeForm.dueDate,
      status: "Pending",
      paidDate: ""
    }]);
    setFeeForm(emptyFeeForm);
  }
  function markFeePaid(id) {
    setFees(prev => prev.map(f => f.id === id ? {
      ...f,
      status: "Paid",
      paidDate: todayStr()
    } : f));
  }
  function markFeePending(id) {
    setFees(prev => prev.map(f => f.id === id ? {
      ...f,
      status: "Pending",
      paidDate: ""
    } : f));
  }

  // ── Mess menu logic ───────────────────────────────────────────────────
  function updateMenu(day, meal, value) {
    setMessMenu(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [meal]: value
      }
    }));
  }

  // ── Staff logic ────────────────────────────────────────────────────────
  function addStaff() {
    setStaffError("");
    if (!staffForm.name.trim()) {
      setStaffError("Name is required.");
      return;
    }
    if (!staffForm.contact.trim() || staffForm.contact.trim().length < 10) {
      setStaffError("Please enter a valid contact number.");
      return;
    }
    setStaff(prev => [...prev, {
      id: newId("s"),
      name: staffForm.name.trim(),
      role: staffForm.role,
      hostel,
      contact: staffForm.contact.trim()
    }]);
    setStaffForm(emptyStaffForm);
  }
  function deleteStaff(id) {
    setStaff(prev => prev.filter(s => s.id !== id));
  }
  return <div style={{
    minHeight: "100vh",
    background: PALE,
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: 24
  }}>
      {/* Header */}
      <div style={{
      background: `linear-gradient(135deg, ${NAVY}, ${BLUE}, ${LIGHT})`,
      borderRadius: 24,
      padding: "22px 28px",
      marginBottom: 20,
      boxShadow: "0 4px 20px rgba(13,71,161,0.25)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 16
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 16
      }}>
          <div style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: "rgba(255,255,255,0.22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 28
        }}><Building2 className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
          <div>
            <div style={{
            fontWeight: 800,
            fontSize: 21,
            color: "#FFFFFF",
            letterSpacing: "-0.3px"
          }}>
              Hostel Management
            </div>
            <div style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.85)",
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            fontWeight: 500
          }}>
              Rooms · Fee · Mess · Complaints · Staff — Boys &amp; Girls hostel
            </div>
          </div>
        </div>

        {/* Boys / Girls toggle */}
        <div style={{
        background: "rgba(255,255,255,0.18)",
        borderRadius: 14,
        padding: 4,
        display: "flex",
        gap: 4
      }}>
          {["Boys", "Girls"].map(h => <button key={h} onClick={() => switchHostel(h)} style={{
          border: "none",
          cursor: "pointer",
          padding: "10px 22px",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 700,
          background: hostel === h ? "#FFFFFF" : "transparent",
          color: hostel === h ? NAVY : "#FFFFFF"
        }}>
              {h === "Boys" ? "Boys Hostel" : "Girls Hostel"}
            </button>)}
        </div>
      </div>

      {/* Tabs */}
      <div style={{
      display: "flex",
      gap: 10,
      marginBottom: 20,
      flexWrap: "wrap"
    }}>
        {[{
        id: "requests",
        label: "Requests"
      }, {
        id: "new",
        label: "New Request"
      }, {
        id: "rooms",
        label: "Rooms"
      }, {
        id: "register",
        label: "Register Student"
      }, {
        id: "complaints",
        label: " Complaints"
      }, {
        id: "fee",
        label: "Fee"
      }, {
        id: "mess",
        label: " Mess Menu"
      }, {
        id: "staff",
        label: "Staff"
      }, {
        id: "inventory",
        label: "Inventory"
      }, {
        id: "messages",
        label: " Messages"
      }].map(t => <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
        border: `1px solid ${activeTab === t.id ? NAVY : LIGHT}`,
        background: activeTab === t.id ? NAVY : "#FFFFFF",
        color: activeTab === t.id ? "#FFFFFF" : NAVY,
        padding: "10px 20px",
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer"
      }}>
            {t.label}
          </button>)}
      </div>

      {/* ══════════════ REQUESTS TAB ══════════════ */}
      {activeTab === "requests" && <div>
          <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
        marginBottom: 20
      }}>
            {[{
          label: "Total Requests",
          value: reqStats.total,
          color: NAVY,
          icon: <ClipboardList className="inline w-[1em] h-[1em] align-[-0.125em]" />
        }, {
          label: "Pending Approval",
          value: reqStats.pending,
          color: "#E65100",
          icon: "⏳"
        }, {
          label: "Approved",
          value: reqStats.approved,
          color: "#2E7D32",
          icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
        }, {
          label: "Currently Out",
          value: reqStats.outNow,
          color: BLUE,
          icon: <Footprints className="inline w-[1em] h-[1em] align-[-0.125em]" />
        }].map(s => <StatCard key={s.label} {...s} />)}
          </div>

          <div style={{
        display: "flex",
        gap: 8,
        marginBottom: 16,
        flexWrap: "wrap"
      }}>
            {["All", "Pending", "Approved", "Rejected"].map(s => <button key={s} onClick={() => setStatusFilter(s)} style={{
          border: `1px solid ${statusFilter === s ? NAVY : LIGHT}`,
          background: statusFilter === s ? PALE : "#FFFFFF",
          color: NAVY,
          padding: "6px 16px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer"
        }}>
                {s} {s !== "All" ? `(${hostelRequests.filter(r => r.status === s).length})` : `(${hostelRequests.length})`}
              </button>)}
          </div>

          {filteredRequests.length === 0 ? <EmptyBox text={`No ${statusFilter !== "All" ? statusFilter.toLowerCase() + " " : ""}requests for the ${hostel} hostel yet.`} /> : <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 12
      }}>
              {filteredRequests.map(r => {
          const sc = STATUS_COLOR[r.status] || STATUS_COLOR.Pending;
          const tc = TYPE_COLOR[r.type] || TYPE_COLOR.Leave;
          const today = todayStr();
          const isOutNow = r.status === "Approved" && !r.returned && (r.type === "Leave" && r.fromDate <= today && today <= r.toDate || r.type === "Permission" && r.date === today);
          const roomNo = rooms.find(rm => rm.id === students.find(s => s.id === r.studentId)?.roomId)?.roomNo;
          return <div key={r.id} style={{
            background: "#FFFFFF",
            borderRadius: 18,
            border: `1px solid ${LIGHT}`,
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            display: "flex"
          }}>
                    <div style={{
              width: 6,
              background: tc.bg,
              flexShrink: 0
            }} />
                    <div style={{
              padding: "16px 20px",
              flex: 1
            }}>
                      <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 10
              }}>
                        <div>
                          <div style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#1A1A1A"
                  }}>
                            {r.studentName}{" "}
                            <span style={{
                      fontWeight: 500,
                      fontSize: 12,
                      color: "#607D8B"
                    }}>
                              · Class {r.className}{r.section ? `-${r.section}` : ""}{roomNo ? ` · Room ${roomNo}` : ""}
                            </span>
                          </div>
                          <div style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 6,
                    alignItems: "center",
                    flexWrap: "wrap"
                  }}>
                            <span style={{
                      background: tc.bg,
                      color: "#FFFFFF",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 20
                    }}>{tc.label}</span>
                            <span style={{
                      background: sc.bg,
                      color: sc.text,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 20,
                      display: "flex",
                      alignItems: "center",
                      gap: 5
                    }}>
                              <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: sc.dot
                      }} /> {r.status}
                            </span>
                            {isOutNow && <span style={{
                      background: "#FFF8E1",
                      color: "#F57F17",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 20
                    }}><Footprints className="inline w-[1em] h-[1em] align-[-0.125em]" /> Out now</span>}
                            {r.status === "Approved" && r.returned && <span style={{
                      background: "#E0F2F1",
                      color: "#00695C",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 20
                    }}><Home className="inline w-[1em] h-[1em] align-[-0.125em]" /> Returned</span>}
                          </div>
                        </div>
                        <div style={{
                  display: "flex",
                  gap: 8,
                  flexShrink: 0
                }}>
                          {r.status === "Pending" && <>
                              <button onClick={() => decide(r.id, "Approved")} style={{
                      background: "#2E7D32",
                      color: "#FFFFFF",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> Approve</button>
                              <button onClick={() => decide(r.id, "Rejected")} style={{
                      background: "#C62828",
                      color: "#FFFFFF",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /> Reject</button>
                            </>}
                          {r.status === "Approved" && <button onClick={() => toggleReturned(r.id, r.returned)} style={{
                    background: r.returned ? "#FFFFFF" : PALE,
                    color: NAVY,
                    border: `1px solid ${NAVY}`,
                    padding: "8px 16px",
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}>
                              {r.returned ? "Mark Not Returned" : "Mark Returned"}
                            </button>}
                        </div>
                      </div>

                      <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: 10,
                marginTop: 14,
                fontSize: 12.5,
                color: "#37474F"
              }}>
                        {r.type === "Leave" ? <><div><b>From:</b> {fmtDate(r.fromDate)}</div><div><b>To:</b> {fmtDate(r.toDate)}</div></> : <>
                            <div><b>Date:</b> {fmtDate(r.date)}</div>
                            <div><b>Out:</b> {r.outTime || "—"}</div>
                            <div><b>Expected in:</b> {r.expectedInTime || "—"}</div>
                            <div><b>Destination:</b> {r.destination || "—"}</div>
                          </>}
                        <div><b>Parent:</b> {r.parentMobile || "—"}</div>
                        <div><b>Requested:</b> {fmtDate(r.createdAt)}</div>
                      </div>

                      <div style={{
                marginTop: 10,
                fontSize: 13,
                color: "#1A1A1A",
                background: PALE,
                borderRadius: 10,
                padding: "8px 12px"
              }}>
                        <b style={{
                  color: NAVY
                }}>Reason: </b>{r.reason}
                      </div>
                      {r.wardenRemarks && <div style={{
                marginTop: 8,
                fontSize: 12.5,
                color: "#607D8B"
              }}><b>Warden remarks:</b> {r.wardenRemarks}</div>}
                    </div>
                  </div>;
        })}
            </div>}
        </div>}

      {/* ══════════════ NEW REQUEST TAB ══════════════ */}
      {activeTab === "new" && <div style={{
      background: "#FFFFFF",
      borderRadius: 20,
      border: `1px solid ${LIGHT}`,
      padding: 24,
      maxWidth: 620,
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
    }}>
          <div style={{
        fontWeight: 800,
        fontSize: 16,
        color: NAVY,
        marginBottom: 4
      }}>New {hostel} Hostel Request</div>
          <div style={{
        fontSize: 12,
        color: "#607D8B",
        marginBottom: 20
      }}>Search a registered student, choose Leave or Permission, and fill in the details.</div>

          <div style={{
        marginBottom: 18,
        position: "relative"
      }}>
            <label style={labelStyle}>STUDENT</label>
            <input value={studentSearch} onChange={e => {
          setStudentSearch(e.target.value);
          setSelectedStudent(null);
        }} placeholder={`Search ${hostel.toLowerCase()} hostel students by name, class or room...`} style={inputStyle} />
            {studentSearch.trim() && !selectedStudent && <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          zIndex: 10,
          background: "#FFFFFF",
          border: `1px solid ${LIGHT}`,
          borderRadius: 12,
          marginTop: 4,
          maxHeight: 220,
          overflowY: "auto",
          boxShadow: "0 6px 20px rgba(0,0,0,0.12)"
        }}>
                {filteredStudents.length === 0 ? <div style={{
            padding: 14,
            fontSize: 13,
            color: "#607D8B"
          }}>No {hostel.toLowerCase()} hostel students match "{studentSearch}". Register them first in the "Register Student" tab.</div> : filteredStudents.map(s => {
            const rn = rooms.find(r => r.id === s.roomId)?.roomNo;
            return <div key={s.id} onClick={() => pickStudent(s)} onMouseDown={e => e.preventDefault()} style={{
              padding: "10px 14px",
              cursor: "pointer",
              fontSize: 13,
              color: "#1A1A1A",
              borderBottom: `1px solid ${PALE}`
            }}>
                      <b>{s.name}</b> <span style={{
                color: "#607D8B"
              }}>· Class {s.className}{s.section ? `-${s.section}` : ""}{rn ? ` · Room ${rn}` : " · No room"}</span>
                    </div>;
          })}
              </div>}
          </div>

          {selectedStudent && <div style={{
        background: PALE,
        borderRadius: 12,
        padding: "10px 14px",
        marginBottom: 18,
        fontSize: 12.5,
        color: NAVY,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
              <span><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> Selected: <b>{selectedStudent.name}</b> · Parent: {selectedStudent.parentNumber}</span>
              <button onClick={() => {
          setSelectedStudent(null);
          setStudentSearch("");
        }} style={{
          background: "none",
          border: "none",
          color: NAVY,
          cursor: "pointer",
          fontWeight: 700
        }}>Change</button>
            </div>}

          <div style={{
        marginBottom: 18
      }}>
            <label style={labelStyle}>REQUEST TYPE</label>
            <div style={{
          display: "flex",
          gap: 8
        }}>
              {["Leave", "Permission"].map(t => <button key={t} onClick={() => setReqForm(f => ({
            ...f,
            type: t
          }))} style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            border: `1px solid ${reqForm.type === t ? NAVY : LIGHT}`,
            background: reqForm.type === t ? NAVY : "#FFFFFF",
            color: reqForm.type === t ? "#FFFFFF" : NAVY
          }}>
                  {t === "Leave" ? "Leave (going home)" : "Permission (same-day outing)"}
                </button>)}
            </div>
          </div>

          {reqForm.type === "Leave" ? <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 14,
        marginBottom: 18
      }}>
              <div><label style={labelStyle}>FROM DATE</label><input type="date" value={reqForm.fromDate} onChange={e => setReqForm(f => ({
            ...f,
            fromDate: e.target.value
          }))} style={inputStyle} /></div>
              <div><label style={labelStyle}>TO DATE</label><input type="date" value={reqForm.toDate} onChange={e => setReqForm(f => ({
            ...f,
            toDate: e.target.value
          }))} style={inputStyle} /></div>
            </div> : <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 14,
        marginBottom: 18
      }}>
              <div><label style={labelStyle}>DATE</label><input type="date" value={reqForm.date} onChange={e => setReqForm(f => ({
            ...f,
            date: e.target.value
          }))} style={inputStyle} /></div>
              <div><label style={labelStyle}>DESTINATION</label><input value={reqForm.destination} onChange={e => setReqForm(f => ({
            ...f,
            destination: e.target.value
          }))} placeholder="e.g. Home, Clinic, Market" style={inputStyle} /></div>
              <div><label style={labelStyle}>OUT TIME</label><input type="time" value={reqForm.outTime} onChange={e => setReqForm(f => ({
            ...f,
            outTime: e.target.value
          }))} style={inputStyle} /></div>
              <div><label style={labelStyle}>EXPECTED RETURN TIME</label><input type="time" value={reqForm.expectedInTime} onChange={e => setReqForm(f => ({
            ...f,
            expectedInTime: e.target.value
          }))} style={inputStyle} /></div>
            </div>}

          <div style={{
        marginBottom: 18
      }}>
            <label style={labelStyle}>REASON</label>
            <textarea value={reqForm.reason} onChange={e => setReqForm(f => ({
          ...f,
          reason: e.target.value
        }))} placeholder="Reason for leave / permission..." rows={3} style={{
          ...inputStyle,
          resize: "vertical",
          fontFamily: "inherit"
        }} />
          </div>

          {reqError && <div style={{
        background: "#FFEBEE",
        color: "#C62828",
        padding: "10px 14px",
        borderRadius: 10,
        fontSize: 13,
        marginBottom: 14
      }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {reqError}</div>}
          {reqSuccess && <div style={{
        background: "#E8F5E9",
        color: "#2E7D32",
        padding: "10px 14px",
        borderRadius: 10,
        fontSize: 13,
        marginBottom: 14
      }}>{reqSuccess}</div>}

          <div style={{
        display: "flex",
        gap: 10
      }}>
            <button onClick={submitRequest} style={{
          flex: 1,
          background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
          color: "#FFFFFF",
          border: "none",
          padding: "13px 0",
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer"
        }}>Submit Request</button>
            <button onClick={resetRequestForm} style={{
          background: "#FFFFFF",
          color: NAVY,
          border: `1px solid ${LIGHT}`,
          padding: "13px 20px",
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer"
        }}>Clear</button>
          </div>
        </div>}

      {/* ══════════════ ROOMS TAB ══════════════ */}
      {activeTab === "rooms" && <div>
          <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
        marginBottom: 20
      }}>
            <StatCard label="Total Rooms" value={roomStats.totalRooms} color={NAVY} icon="" />
            <StatCard label="Total Capacity" value={roomStats.totalCapacity} color={BLUE} icon="" />
            <StatCard label="Occupied" value={roomStats.totalOccupied} color="#E65100" icon="" />
            <StatCard label="Available Beds" value={roomStats.available} color="#2E7D32" icon="" />
          </div>

          <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 20,
        alignItems: "start"
      }}>
            {/* Rooms list */}
            <div>
              <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginBottom: 20
          }}>
                {hostelRooms.length === 0 ? <EmptyBox text={`No rooms added yet for the ${hostel} hostel.`} /> : hostelRooms.map(r => {
              const occ = occupancyByRoom[r.id] || 0;
              const pct = Math.min(100, Math.round(occ / r.capacity * 100));
              const occupants = hostelStudents.filter(s => s.roomId === r.id);
              const full = occ >= r.capacity;
              return <div key={r.id} style={{
                background: "#FFFFFF",
                borderRadius: 16,
                border: `1px solid ${LIGHT}`,
                padding: "16px 18px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
              }}>
                      <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8
                }}>
                        <div>
                          <span style={{
                      fontWeight: 800,
                      fontSize: 15,
                      color: NAVY
                    }}>Room {r.roomNo}</span>
                          <span style={{
                      fontSize: 12,
                      color: "#607D8B",
                      marginLeft: 8
                    }}>{r.floor}</span>
                        </div>
                        <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10
                  }}>
                          <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 20,
                      background: full ? "#FFEBEE" : "#E8F5E9",
                      color: full ? "#C62828" : "#2E7D32"
                    }}>{occ}/{r.capacity} {full ? "FULL" : "beds used"}</span>
                          <button onClick={() => deleteRoom(r.id)} style={{
                      background: "none",
                      border: "none",
                      color: "#C62828",
                      cursor: "pointer",
                      fontSize: 13
                    }} title="Delete room"><Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
                        </div>
                      </div>
                      <div style={{
                  height: 6,
                  background: PALE,
                  borderRadius: 4,
                  overflow: "hidden",
                  marginBottom: 10
                }}>
                        <div style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: full ? "#C62828" : BLUE,
                    borderRadius: 4
                  }} />
                      </div>
                      {r.notes && <div style={{
                  fontSize: 12,
                  color: "#607D8B",
                  marginBottom: 8
                }}>ℹ {r.notes}</div>}
                      <div style={{
                  fontSize: 12.5,
                  color: "#37474F"
                }}>
                        <b>Occupants:</b> {occupants.length === 0 ? "— empty —" : occupants.map(o => o.name).join(", ")}
                      </div>
                    </div>;
            })}
              </div>

              {/* Add room form */}
              <div style={{
            background: "#FFFFFF",
            borderRadius: 16,
            border: `1px solid ${LIGHT}`,
            padding: 18
          }}>
                <div style={{
              fontWeight: 700,
              fontSize: 14,
              color: NAVY,
              marginBottom: 12
            }}><Plus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Add a Room</div>
                <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 10,
              marginBottom: 10
            }}>
                  <input placeholder="Room No (e.g. 104)" value={roomForm.roomNo} onChange={e => setRoomForm(f => ({
                ...f,
                roomNo: e.target.value
              }))} style={inputStyle} />
                  <input placeholder="Floor (e.g. 2nd Floor)" value={roomForm.floor} onChange={e => setRoomForm(f => ({
                ...f,
                floor: e.target.value
              }))} style={inputStyle} />
                  <input type="number" min="1" placeholder="Capacity" value={roomForm.capacity} onChange={e => setRoomForm(f => ({
                ...f,
                capacity: e.target.value
              }))} style={inputStyle} />
                </div>
                <input placeholder="Notes (optional, e.g. near washroom)" value={roomForm.notes} onChange={e => setRoomForm(f => ({
              ...f,
              notes: e.target.value
            }))} style={{
              ...inputStyle,
              marginBottom: 10
            }} />
                {roomError && <div style={{
              background: "#FFEBEE",
              color: "#C62828",
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 12.5,
              marginBottom: 10
            }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {roomError}</div>}
                <button onClick={addRoom} style={{
              background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
              color: "#FFFFFF",
              border: "none",
              padding: "10px 20px",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer"
            }}>Add Room</button>
              </div>
            </div>

            {/* Common instructions */}
            <div style={{
          background: "#FFFFFF",
          borderRadius: 16,
          border: `1px solid ${LIGHT}`,
          padding: 18,
          position: "sticky",
          top: 24
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: NAVY,
            marginBottom: 4
          }}><Pin className="inline w-[1em] h-[1em] align-[-0.125em]" /> Common Hostel Rules</div>
              <div style={{
            fontSize: 11.5,
            color: "#607D8B",
            marginBottom: 12
          }}>Applies to every room in this hostel</div>
              <ul style={{
            margin: 0,
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginBottom: 14
          }}>
                {rules.map((rule, i) => <li key={i} style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              fontSize: 12.5,
              color: "#37474F",
              background: PALE,
              borderRadius: 10,
              padding: "8px 10px"
            }}>
                    <span style={{
                flex: 1
              }}>• {rule}</span>
                    <button onClick={() => removeRule(i)} style={{
                background: "none",
                border: "none",
                color: "#C62828",
                cursor: "pointer",
                fontSize: 12,
                flexShrink: 0
              }}><X className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
                  </li>)}
              </ul>
              <div style={{
            display: "flex",
            gap: 8
          }}>
                <input value={newRule} onChange={e => setNewRule(e.target.value)} placeholder="Add a rule..." style={{
              ...inputStyle,
              fontSize: 12.5
            }} />
                <button onClick={addRule} style={{
              background: NAVY,
              color: "#FFFFFF",
              border: "none",
              padding: "0 16px",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer"
            }}>Add</button>
              </div>
            </div>
          </div>
        </div>}

      {/* ══════════════ REGISTER STUDENT TAB ══════════════ */}
      {activeTab === "register" && <div style={{
      display: "grid",
      gridTemplateColumns: "1fr 340px",
      gap: 20,
      alignItems: "start"
    }}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 20,
        border: `1px solid ${LIGHT}`,
        padding: 24,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
      }}>
            <div style={{
          fontWeight: 800,
          fontSize: 16,
          color: NAVY,
          marginBottom: 4
        }}>Register a New {hostel} Hostel Student</div>
            <div style={{
          fontSize: 12,
          color: "#607D8B",
          marginBottom: 20
        }}>Fill in the student's details and allot a room.</div>

            <div style={{
          marginBottom: 16
        }}>
              <label style={labelStyle}>STUDENT NAME</label>
              <input value={regForm.name} onChange={e => setRegForm(f => ({
            ...f,
            name: e.target.value
          }))} placeholder="Full name" style={inputStyle} />
            </div>

            <div style={{
          marginBottom: 16
        }}>
              <label style={labelStyle}>ADDRESS</label>
              <textarea value={regForm.address} onChange={e => setRegForm(f => ({
            ...f,
            address: e.target.value
          }))} placeholder="Home address" rows={2} style={{
            ...inputStyle,
            resize: "vertical",
            fontFamily: "inherit"
          }} />
            </div>

            <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
          marginBottom: 16
        }}>
              <div>
                <label style={labelStyle}>PARENT NUMBER</label>
                <input value={regForm.parentNumber} onChange={e => setRegForm(f => ({
              ...f,
              parentNumber: e.target.value
            }))} placeholder="10-digit mobile number" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>ALTERNATIVE NUMBER</label>
                <input value={regForm.altNumber} onChange={e => setRegForm(f => ({
              ...f,
              altNumber: e.target.value
            }))} placeholder="Optional" style={inputStyle} />
              </div>
            </div>

            <div style={{
          marginBottom: 16
        }}>
              <label style={labelStyle}>SCHOOL NAME</label>
              <input value={regForm.schoolName} onChange={e => setRegForm(f => ({
            ...f,
            schoolName: e.target.value
          }))} placeholder="School / institution name" style={inputStyle} />
            </div>

            <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
          marginBottom: 16
        }}>
              <div>
                <label style={labelStyle}>CLASS</label>
                <input value={regForm.className} onChange={e => setRegForm(f => ({
              ...f,
              className: e.target.value
            }))} placeholder="e.g. 9" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>SECTION</label>
                <input value={regForm.section} onChange={e => setRegForm(f => ({
              ...f,
              section: e.target.value
            }))} placeholder="e.g. A" style={inputStyle} />
              </div>
            </div>

            <div style={{
          marginBottom: 18
        }}>
              <label style={labelStyle}>ALLOT ROOM</label>
              <select value={regForm.roomId} onChange={e => setRegForm(f => ({
            ...f,
            roomId: e.target.value
          }))} style={{
            ...inputStyle,
            cursor: "pointer"
          }}>
                <option value="">— Select a room —</option>
                {hostelRooms.map(r => {
              const occ = occupancyByRoom[r.id] || 0;
              const full = occ >= r.capacity;
              return <option key={r.id} value={r.id} disabled={full}>{roomLabel(r)}</option>;
            })}
              </select>
              {hostelRooms.length === 0 && <div style={{
            fontSize: 12,
            color: "#E65100",
            marginTop: 6
          }}>No rooms exist yet — add one in the "Rooms" tab first.</div>}
            </div>

            {regError && <div style={{
          background: "#FFEBEE",
          color: "#C62828",
          padding: "10px 14px",
          borderRadius: 10,
          fontSize: 13,
          marginBottom: 14
        }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {regError}</div>}
            {regSuccess && <div style={{
          background: "#E8F5E9",
          color: "#2E7D32",
          padding: "10px 14px",
          borderRadius: 10,
          fontSize: 13,
          marginBottom: 14
        }}>{regSuccess}</div>}

            <div style={{
          display: "flex",
          gap: 10
        }}>
              <button onClick={submitRegistration} style={{
            flex: 1,
            background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
            color: "#FFFFFF",
            border: "none",
            padding: "13px 0",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer"
          }}>Register &amp; Allot Room</button>
              <button onClick={() => {
            setRegForm(emptyRegForm);
            setRegError("");
            setRegSuccess("");
          }} style={{
            background: "#FFFFFF",
            color: NAVY,
            border: `1px solid ${LIGHT}`,
            padding: "13px 20px",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer"
          }}>Clear</button>
            </div>
          </div>

          {/* Quick room availability sidebar */}
          <div style={{
        background: "#FFFFFF",
        borderRadius: 20,
        border: `1px solid ${LIGHT}`,
        padding: 18,
        position: "sticky",
        top: 24
      }}>
            <div style={{
          fontWeight: 700,
          fontSize: 14,
          color: NAVY,
          marginBottom: 12
        }}><BedDouble className="inline w-[1em] h-[1em] align-[-0.125em]" /> Room Availability</div>
            <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 8
        }}>
              {hostelRooms.length === 0 ? <div style={{
            fontSize: 12.5,
            color: "#607D8B"
          }}>No rooms yet.</div> : hostelRooms.map(r => {
            const occ = occupancyByRoom[r.id] || 0;
            const full = occ >= r.capacity;
            return <div key={r.id} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 12px",
              borderRadius: 10,
              background: full ? "#FFEBEE" : PALE,
              fontSize: 12.5
            }}>
                    <span style={{
                color: "#1A1A1A",
                fontWeight: 600
              }}>Room {r.roomNo}</span>
                    <span style={{
                color: full ? "#C62828" : "#2E7D32",
                fontWeight: 700
              }}>{occ}/{r.capacity}</span>
                  </div>;
          })}
            </div>
          </div>
        </div>}

      {/* ══════════════ COMPLAINTS TAB ══════════════ */}
      {activeTab === "complaints" && <div>
          <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
        marginBottom: 20
      }}>
            <StatCard label="Total Complaints" value={complaintStats.total} color={NAVY} icon="" />
            <StatCard label="Pending" value={complaintStats.pending} color="#E65100" icon="⏳" />
            <StatCard label="In Progress" value={complaintStats.inProgress} color={BLUE} icon="" />
            <StatCard label="Resolved" value={complaintStats.resolved} color="#2E7D32" icon="" />
          </div>

          <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 20,
        alignItems: "start"
      }}>
            <div>
              <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 16,
            flexWrap: "wrap"
          }}>
                {["All", "Pending", "In Progress", "Resolved"].map(s => <button key={s} onClick={() => setComplaintFilter(s)} style={{
              border: `1px solid ${complaintFilter === s ? NAVY : LIGHT}`,
              background: complaintFilter === s ? PALE : "#FFFFFF",
              color: NAVY,
              padding: "6px 16px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer"
            }}>
                    {s} {s !== "All" ? `(${hostelComplaints.filter(c => c.status === s).length})` : `(${hostelComplaints.length})`}
                  </button>)}
              </div>

              {filteredComplaints.length === 0 ? <EmptyBox text={`No ${complaintFilter !== "All" ? complaintFilter.toLowerCase() + " " : ""}complaints for the ${hostel} hostel.`} /> : <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 12
          }}>
                  {filteredComplaints.map(c => {
              const student = studentById(c.studentId);
              const sc = COMPLAINT_STATUS_COLOR[c.status] || COMPLAINT_STATUS_COLOR.Pending;
              return <div key={c.id} style={{
                background: "#FFFFFF",
                borderRadius: 16,
                border: `1px solid ${LIGHT}`,
                padding: "16px 18px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
              }}>
                        <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 10
                }}>
                          <div>
                            <div style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: "#1A1A1A"
                    }}>
                              {CATEGORY_ICON[c.category] || <Wrench className="inline w-[1em] h-[1em] align-[-0.125em]" />} {c.category}
                              <span style={{
                        fontWeight: 500,
                        fontSize: 12,
                        color: "#607D8B"
                      }}>
                                {" "}· {student?.name || "Unknown"}{roomNoFor(student) ? ` · Room ${roomNoFor(student)}` : ""}
                              </span>
                            </div>
                            <span style={{
                      background: sc.bg,
                      color: sc.text,
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: 20,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      marginTop: 6
                    }}>
                              <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: sc.dot
                      }} /> {c.status}
                            </span>
                          </div>
                          <div style={{
                    display: "flex",
                    gap: 8
                  }}>
                            {c.status !== "In Progress" && <button onClick={() => updateComplaintStatus(c.id, "In Progress")} style={{
                      background: BLUE,
                      color: "#FFFFFF",
                      border: "none",
                      padding: "7px 14px",
                      borderRadius: 10,
                      fontSize: 11.5,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}>Mark In Progress</button>}
                            {c.status !== "Resolved" && <button onClick={() => updateComplaintStatus(c.id, "Resolved")} style={{
                      background: "#2E7D32",
                      color: "#FFFFFF",
                      border: "none",
                      padding: "7px 14px",
                      borderRadius: 10,
                      fontSize: 11.5,
                      fontWeight: 700,
                      cursor: "pointer"
                    }}>Mark Resolved</button>}
                          </div>
                        </div>

                        <div style={{
                  marginTop: 10,
                  fontSize: 13,
                  color: "#1A1A1A",
                  background: PALE,
                  borderRadius: 10,
                  padding: "8px 12px"
                }}>
                          {c.description}
                        </div>

                        <div style={{
                  marginTop: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap"
                }}>
                          <span style={{
                    fontSize: 12,
                    color: "#607D8B"
                  }}><HardHat className="inline w-[1em] h-[1em] align-[-0.125em]" /> Assigned: <b style={{
                      color: "#37474F"
                    }}>{c.assignedTo || "Not assigned"}</b></span>
                          <input value={assignInputs[c.id] || ""} onChange={e => setAssignInputs(prev => ({
                    ...prev,
                    [c.id]: e.target.value
                  }))} placeholder="Staff name..." style={{
                    ...inputStyle,
                    width: 140,
                    padding: "6px 10px",
                    fontSize: 12
                  }} />
                          <button onClick={() => assignStaffToComplaint(c.id)} style={{
                    background: NAVY,
                    color: "#FFFFFF",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: 10,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}>Assign</button>
                        </div>
                      </div>;
            })}
                </div>}
            </div>

            {/* Raise complaint form */}
            <div style={{
          background: "#FFFFFF",
          borderRadius: 16,
          border: `1px solid ${LIGHT}`,
          padding: 18,
          position: "sticky",
          top: 24
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: NAVY,
            marginBottom: 12
          }}><Plus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Raise a Complaint</div>
              <div style={{
            marginBottom: 12
          }}>
                <label style={labelStyle}>STUDENT</label>
                <select value={complaintForm.studentId} onChange={e => setComplaintForm(f => ({
              ...f,
              studentId: e.target.value
            }))} style={{
              ...inputStyle,
              cursor: "pointer"
            }}>
                  <option value="">— Select student —</option>
                  {hostelStudents.map(s => {
                const rn = rooms.find(r => r.id === s.roomId)?.roomNo;
                return <option key={s.id} value={s.id}>{s.name}{rn ? ` (Room ${rn})` : ""}</option>;
              })}
                </select>
              </div>
              <div style={{
            marginBottom: 12
          }}>
                <label style={labelStyle}>CATEGORY</label>
                <select value={complaintForm.category} onChange={e => setComplaintForm(f => ({
              ...f,
              category: e.target.value
            }))} style={{
              ...inputStyle,
              cursor: "pointer"
            }}>
                  {Object.keys(CATEGORY_ICON).map(cat => <option key={cat} value={cat}>{CATEGORY_ICON[cat]} {cat}</option>)}
                </select>
              </div>
              <div style={{
            marginBottom: 12
          }}>
                <label style={labelStyle}>DESCRIPTION</label>
                <textarea value={complaintForm.description} onChange={e => setComplaintForm(f => ({
              ...f,
              description: e.target.value
            }))} placeholder="Describe the issue..." rows={3} style={{
              ...inputStyle,
              resize: "vertical",
              fontFamily: "inherit"
            }} />
              </div>
              {complaintError && <div style={{
            background: "#FFEBEE",
            color: "#C62828",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 12.5,
            marginBottom: 10
          }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {complaintError}</div>}
              <button onClick={submitComplaint} style={{
            width: "100%",
            background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
            color: "#FFFFFF",
            border: "none",
            padding: "11px 0",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer"
          }}>Submit Complaint</button>
            </div>
          </div>
        </div>}

      {/* ══════════════ FEE TAB ══════════════ */}
      {activeTab === "fee" && <div>
          <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
        marginBottom: 20
      }}>
            <StatCard label="Collected" value={fmtMoney(feeStats.collected)} color="#2E7D32" icon="" />
            <StatCard label="Pending Amount" value={fmtMoney(feeStats.pendingAmt)} color="#E65100" icon="⏳" />
            <StatCard label="Overdue Records" value={feeStats.overdue} color="#C62828" icon="" />
            <StatCard label="Total Records" value={feeStats.count} color={NAVY} icon="" />
          </div>

          <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: 20,
        alignItems: "start"
      }}>
            <div>
              <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 16,
            flexWrap: "wrap"
          }}>
                {["All", "Paid", "Pending", "Overdue"].map(s => <button key={s} onClick={() => setFeeFilter(s)} style={{
              border: `1px solid ${feeFilter === s ? NAVY : LIGHT}`,
              background: feeFilter === s ? PALE : "#FFFFFF",
              color: NAVY,
              padding: "6px 16px",
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer"
            }}>
                    {s} {s !== "All" ? `(${displayFees.filter(f => f.displayStatus === s).length})` : `(${displayFees.length})`}
                  </button>)}
              </div>

              {filteredFees.length === 0 ? <EmptyBox text={`No ${feeFilter !== "All" ? feeFilter.toLowerCase() + " " : ""}fee records for the ${hostel} hostel.`} /> : <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 10
          }}>
                  {filteredFees.map(f => {
              const student = studentById(f.studentId);
              const sc = FEE_STATUS_COLOR[f.displayStatus] || FEE_STATUS_COLOR.Pending;
              return <div key={f.id} style={{
                background: "#FFFFFF",
                borderRadius: 14,
                border: `1px solid ${LIGHT}`,
                padding: "14px 18px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
              }}>
                        <div>
                          <div style={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#1A1A1A"
                  }}>{student?.name || "Unknown"}</div>
                          <div style={{
                    fontSize: 12,
                    color: "#607D8B",
                    marginTop: 2
                  }}>{f.term} · Due {fmtDate(f.dueDate)}{f.paidDate ? ` · Paid ${fmtDate(f.paidDate)}` : ""}</div>
                        </div>
                        <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12
                }}>
                          <span style={{
                    fontWeight: 800,
                    fontSize: 15,
                    color: NAVY
                  }}>{fmtMoney(f.amount)}</span>
                          <span style={{
                    background: sc.bg,
                    color: sc.text,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 10px",
                    borderRadius: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 5
                  }}>
                            <span style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: sc.dot
                    }} /> {f.displayStatus}
                          </span>
                          {f.status === "Pending" ? <button onClick={() => markFeePaid(f.id)} style={{
                    background: "#2E7D32",
                    color: "#FFFFFF",
                    border: "none",
                    padding: "7px 14px",
                    borderRadius: 10,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}>Mark Paid</button> : <button onClick={() => markFeePending(f.id)} style={{
                    background: "#FFFFFF",
                    color: NAVY,
                    border: `1px solid ${NAVY}`,
                    padding: "7px 14px",
                    borderRadius: 10,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer"
                  }}>Undo</button>}
                        </div>
                      </div>;
            })}
                </div>}
            </div>

            {/* Add fee record form */}
            <div style={{
          background: "#FFFFFF",
          borderRadius: 16,
          border: `1px solid ${LIGHT}`,
          padding: 18,
          position: "sticky",
          top: 24
        }}>
              <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: NAVY,
            marginBottom: 12
          }}><Plus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Add Fee Record</div>
              <div style={{
            marginBottom: 12
          }}>
                <label style={labelStyle}>STUDENT</label>
                <select value={feeForm.studentId} onChange={e => setFeeForm(f => ({
              ...f,
              studentId: e.target.value
            }))} style={{
              ...inputStyle,
              cursor: "pointer"
            }}>
                  <option value="">— Select student —</option>
                  {hostelStudents.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{
            marginBottom: 12
          }}>
                <label style={labelStyle}>TERM</label>
                <select value={feeForm.term} onChange={e => setFeeForm(f => ({
              ...f,
              term: e.target.value
            }))} style={{
              ...inputStyle,
              cursor: "pointer"
            }}>
                  {FEE_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 10,
            marginBottom: 12
          }}>
                <div>
                  <label style={labelStyle}>AMOUNT (₹)</label>
                  <input type="number" value={feeForm.amount} onChange={e => setFeeForm(f => ({
                ...f,
                amount: e.target.value
              }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>DUE DATE</label>
                  <input type="date" value={feeForm.dueDate} onChange={e => setFeeForm(f => ({
                ...f,
                dueDate: e.target.value
              }))} style={inputStyle} />
                </div>
              </div>
              {feeError && <div style={{
            background: "#FFEBEE",
            color: "#C62828",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 12.5,
            marginBottom: 10
          }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {feeError}</div>}
              <button onClick={addFeeRecord} style={{
            width: "100%",
            background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
            color: "#FFFFFF",
            border: "none",
            padding: "11px 0",
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer"
          }}>Add Record</button>
            </div>
          </div>
        </div>}

      {/* ══════════════ MESS MENU TAB ══════════════ */}
      {activeTab === "mess" && <div>
          <div style={{
        display: "flex",
        gap: 8,
        marginBottom: 20,
        flexWrap: "wrap"
      }}>
            {DAYS.map(d => <button key={d} onClick={() => setMessDay(d)} style={{
          border: `1px solid ${messDay === d ? NAVY : LIGHT}`,
          background: messDay === d ? NAVY : "#FFFFFF",
          color: messDay === d ? "#FFFFFF" : NAVY,
          padding: "8px 16px",
          borderRadius: 12,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: "pointer"
        }}>
                {d}
              </button>)}
          </div>

          <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 16,
        marginBottom: 24
      }}>
            {MEALS.map(m => <div key={m.key} style={{
          background: "#FFFFFF",
          borderRadius: 18,
          border: `1px solid ${LIGHT}`,
          padding: 18,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
        }}>
                <div style={{
            fontWeight: 700,
            fontSize: 14,
            color: NAVY,
            marginBottom: 10
          }}>{m.label}</div>
                <textarea value={messMenu[messDay]?.[m.key] || ""} onChange={e => updateMenu(messDay, m.key, e.target.value)} rows={3} style={{
            ...inputStyle,
            resize: "vertical",
            fontFamily: "inherit"
          }} placeholder={`${m.label} items for ${messDay}...`} />
              </div>)}
          </div>

          <div style={{
        fontWeight: 700,
        fontSize: 14,
        color: NAVY,
        marginBottom: 12
      }}><Calendar className="inline w-[1em] h-[1em] align-[-0.125em]" /> Full Week at a Glance</div>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 18,
        border: `1px solid ${LIGHT}`,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
      }}>
            <div style={{
          display: "grid",
          gridTemplateColumns: "110px 1fr 1fr 1fr",
          background: PALE,
          padding: "10px 16px",
          fontSize: 11.5,
          fontWeight: 700,
          color: NAVY,
          letterSpacing: "0.4px"
        }}>
              <div>DAY</div><div>BREAKFAST</div><div>LUNCH</div><div>DINNER</div>
            </div>
            {DAYS.map((d, i) => <div key={d} style={{
          display: "grid",
          gridTemplateColumns: "110px 1fr 1fr 1fr",
          padding: "10px 16px",
          fontSize: 12.5,
          color: "#37474F",
          borderTop: i > 0 ? `1px solid ${PALE}` : "none",
          background: d === messDay ? "#F5FAFF" : "#FFFFFF"
        }}>
                <div style={{
            fontWeight: 700,
            color: "#1A1A1A"
          }}>{d}</div>
                <div>{messMenu[d]?.breakfast || "—"}</div>
                <div>{messMenu[d]?.lunch || "—"}</div>
                <div>{messMenu[d]?.dinner || "—"}</div>
              </div>)}
          </div>
        </div>}

      {/* ══════════════ STAFF TAB ══════════════ */}
      {activeTab === "staff" && <div style={{
      display: "grid",
      gridTemplateColumns: "2fr 1fr",
      gap: 20,
      alignItems: "start"
    }}>
          <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 12
      }}>
            {hostelStaff.length === 0 ? <EmptyBox text={`No staff added yet for the ${hostel} hostel.`} /> : hostelStaff.map(s => <div key={s.id} style={{
          background: "#FFFFFF",
          borderRadius: 16,
          border: `1px solid ${LIGHT}`,
          padding: "14px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
        }}>
                <div style={{
            display: "flex",
            alignItems: "center",
            gap: 14
          }}>
                  <div style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: PALE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18
            }}>
                    {s.role === "Warden" ? "" : s.role === "Security" ? "" : s.role === "Matron" ? "" : s.role === "Cook" ? "" : <Brush className="inline w-[1em] h-[1em] align-[-0.125em]" />}
                  </div>
                  <div>
                    <div style={{
                fontWeight: 700,
                fontSize: 14,
                color: "#1A1A1A"
              }}>{s.name}</div>
                    <div style={{
                fontSize: 12,
                color: "#607D8B"
              }}>{s.role} · <Phone className="inline w-[1em] h-[1em] align-[-0.125em]" /> {s.contact}</div>
                  </div>
                </div>
                <button onClick={() => deleteStaff(s.id)} style={{
            background: "none",
            border: "none",
            color: "#C62828",
            cursor: "pointer",
            fontSize: 14
          }} title="Remove staff"><Trash2 className="inline w-[1em] h-[1em] align-[-0.125em]" /></button>
              </div>)}
          </div>

          <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: `1px solid ${LIGHT}`,
        padding: 18,
        position: "sticky",
        top: 24
      }}>
            <div style={{
          fontWeight: 700,
          fontSize: 14,
          color: NAVY,
          marginBottom: 12
        }}><Plus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Add Staff Member</div>
            <div style={{
          marginBottom: 12
        }}>
              <label style={labelStyle}>NAME</label>
              <input value={staffForm.name} onChange={e => setStaffForm(f => ({
            ...f,
            name: e.target.value
          }))} placeholder="Full name" style={inputStyle} />
            </div>
            <div style={{
          marginBottom: 12
        }}>
              <label style={labelStyle}>ROLE</label>
              <select value={staffForm.role} onChange={e => setStaffForm(f => ({
            ...f,
            role: e.target.value
          }))} style={{
            ...inputStyle,
            cursor: "pointer"
          }}>
                {["Warden", "Security", "Matron", "Cook", "Cleaner", "Other"].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{
          marginBottom: 12
        }}>
              <label style={labelStyle}>CONTACT NUMBER</label>
              <input value={staffForm.contact} onChange={e => setStaffForm(f => ({
            ...f,
            contact: e.target.value
          }))} placeholder="10-digit mobile number" style={inputStyle} />
            </div>
            {staffError && <div style={{
          background: "#FFEBEE",
          color: "#C62828",
          padding: "8px 12px",
          borderRadius: 8,
          fontSize: 12.5,
          marginBottom: 10
        }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {staffError}</div>}
            <button onClick={addStaff} style={{
          width: "100%",
          background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
          color: "#FFFFFF",
          border: "none",
          padding: "11px 0",
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer"
        }}>Add Staff</button>
          </div>
        </div>}

      {/* ══════════════ INVENTORY TAB ══════════════ */}
      {activeTab === "inventory" && <div>
          <div style={{
        fontSize: 12,
        color: "#607D8B",
        marginBottom: 16
      }}>
            Furniture &amp; fittings count per room for the {hostel} hostel. Edit any number to update the count.
          </div>

          <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
        gap: 12,
        marginBottom: 24
      }}>
            {INVENTORY_ITEMS.map(item => <StatCard key={item.key} label={item.label} value={hostelInventoryTotals[item.key] || 0} color={NAVY} icon={item.icon} />)}
          </div>

          {hostelRooms.length === 0 ? <EmptyBox text={`No rooms added yet for the ${hostel} hostel — add rooms in the "Rooms" tab first.`} /> : <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: `1px solid ${LIGHT}`,
        overflow: "auto",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
      }}>
              <table style={{
          borderCollapse: "collapse",
          width: "100%",
          minWidth: 780
        }}>
                <thead>
                  <tr style={{
              background: PALE
            }}>
                    <th style={{
                ...thStyle,
                position: "sticky",
                left: 0,
                background: PALE,
                zIndex: 1,
                minWidth: 150
              }}>ROOM</th>
                    {INVENTORY_ITEMS.map(item => <th key={item.key} style={thStyle}>{item.icon} {item.label}</th>)}
                    <th style={thStyle}>STUDENTS</th>
                  </tr>
                </thead>
                <tbody>
                  {hostelRooms.map((r, ri) => {
              const inv = roomInventory[r.id] || blankInventory();
              return <tr key={r.id} style={{
                borderTop: ri > 0 ? `1px solid ${PALE}` : "none"
              }}>
                        <td style={{
                  ...tdStyle,
                  position: "sticky",
                  left: 0,
                  background: "#FFFFFF",
                  zIndex: 1
                }}>
                          <div style={{
                    fontWeight: 800,
                    color: NAVY,
                    fontSize: 13
                  }}>Room {r.roomNo}</div>
                          <div style={{
                    fontSize: 11,
                    color: "#607D8B"
                  }}>{r.floor} · Cap {r.capacity}</div>
                        </td>
                        {INVENTORY_ITEMS.map(item => <td key={item.key} style={tdStyle}>
                            <input type="number" min="0" value={inv[item.key] ?? 0} onChange={e => updateInventoryCount(r.id, item.key, e.target.value)} style={{
                    ...inputStyle,
                    width: 64,
                    textAlign: "center",
                    padding: "8px 6px"
                  }} />
                          </td>)}
                        <td style={{
                  ...tdStyle,
                  fontSize: 12,
                  color: "#607D8B",
                  fontWeight: 700
                }}>
                          {occupancyByRoom[r.id] || 0}/{r.capacity}
                        </td>
                      </tr>;
            })}
                </tbody>
                <tfoot>
                  <tr style={{
              borderTop: `2px solid ${LIGHT}`,
              background: PALE
            }}>
                    <td style={{
                ...tdStyle,
                position: "sticky",
                left: 0,
                background: PALE,
                fontWeight: 800,
                color: NAVY
              }}>TOTAL</td>
                    {INVENTORY_ITEMS.map(item => <td key={item.key} style={{
                ...tdStyle,
                fontWeight: 800,
                color: NAVY,
                textAlign: "center"
              }}>
                        {hostelInventoryTotals[item.key] || 0}
                      </td>)}
                    <td style={tdStyle}></td>
                  </tr>
                </tfoot>
              </table>
            </div>}
        </div>}

      {/* ══════════════ MESSAGES TAB (Principal <-> Warden) ══════════════ */}
      {activeTab === "messages" && <div style={{
      display: "grid",
      gridTemplateColumns: "1fr 300px",
      gap: 20,
      alignItems: "start"
    }}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 20,
        border: `1px solid ${LIGHT}`,
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        height: 560
      }}>
            <div style={{
          padding: "14px 20px",
          borderBottom: `1px solid ${PALE}`
        }}>
              <div style={{
            fontWeight: 800,
            fontSize: 15,
            color: NAVY
          }}>Principal <ArrowLeftRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> Warden</div>
              <div style={{
            fontSize: 11.5,
            color: "#607D8B"
          }}>{hostel} Hostel conversation</div>
            </div>

            <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "18px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 12
        }}>
              {hostelMessages.length === 0 ? <EmptyBox text="No messages yet. Start the conversation below." /> : hostelMessages.map(m => {
            const isPrincipal = m.from === "Principal";
            return <div key={m.id} style={{
              display: "flex",
              justifyContent: isPrincipal ? "flex-end" : "flex-start"
            }}>
                    <div style={{
                maxWidth: "72%"
              }}>
                      <div style={{
                  background: isPrincipal ? `linear-gradient(135deg, ${NAVY}, ${BLUE})` : PALE,
                  color: isPrincipal ? "#FFFFFF" : "#1A1A1A",
                  borderRadius: 16,
                  borderBottomRightRadius: isPrincipal ? 4 : 16,
                  borderBottomLeftRadius: isPrincipal ? 16 : 4,
                  padding: "10px 14px",
                  fontSize: 13,
                  lineHeight: 1.5
                }}>
                        {m.priority === "Urgent" && <div style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: "0.4px",
                    marginBottom: 4,
                    color: isPrincipal ? "#FFE0B2" : "#C62828"
                  }}>
                            <Circle className="inline w-[1em] h-[1em] align-[-0.125em]" /> URGENT
                          </div>}
                        {m.body}
                      </div>
                      <div style={{
                  fontSize: 10.5,
                  color: "#90A4AE",
                  marginTop: 4,
                  textAlign: isPrincipal ? "right" : "left"
                }}>
                        {isPrincipal ? "Principal" : "Warden"} · {fmtDate(m.createdAt)}
                      </div>
                    </div>
                  </div>;
          })}
            </div>

            <div style={{
          padding: "14px 20px",
          borderTop: `1px solid ${PALE}`
        }}>
              {messageError && <div style={{
            background: "#FFEBEE",
            color: "#C62828",
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 12.5,
            marginBottom: 10
          }}><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /> {messageError}</div>}
              <div style={{
            display: "flex",
            gap: 10
          }}>
                <textarea value={messageForm.body} onChange={e => setMessageForm(f => ({
              ...f,
              body: e.target.value
            }))} placeholder={`Message as ${senderRole}...`} rows={2} style={{
              ...inputStyle,
              resize: "vertical",
              fontFamily: "inherit",
              flex: 1
            }} onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }} />
                <button onClick={sendMessage} style={{
              background: `linear-gradient(135deg, ${NAVY}, ${BLUE})`,
              color: "#FFFFFF",
              border: "none",
              padding: "0 22px",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer"
            }}>Send</button>
              </div>
            </div>
          </div>

          {/* Sender + priority controls */}
          <div style={{
        background: "#FFFFFF",
        borderRadius: 16,
        border: `1px solid ${LIGHT}`,
        padding: 18,
        position: "sticky",
        top: 24
      }}>
            <div style={{
          fontWeight: 700,
          fontSize: 14,
          color: NAVY,
          marginBottom: 12
        }}><PenLine className="inline w-[1em] h-[1em] align-[-0.125em]" /> Sending As</div>
            <div style={{
          display: "flex",
          gap: 8,
          marginBottom: 18
        }}>
              {["Principal", "Warden"].map(role => <button key={role} onClick={() => setSenderRole(role)} style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 12,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: "pointer",
            border: `1px solid ${senderRole === role ? NAVY : LIGHT}`,
            background: senderRole === role ? NAVY : "#FFFFFF",
            color: senderRole === role ? "#FFFFFF" : NAVY
          }}>
                  {role === "Principal" ? "Principal" : "Warden"}
                </button>)}
            </div>

            <div style={{
          fontWeight: 700,
          fontSize: 14,
          color: NAVY,
          marginBottom: 10
        }}><Zap className="inline w-[1em] h-[1em] align-[-0.125em]" /> Priority</div>
            <div style={{
          display: "flex",
          gap: 8
        }}>
              {["Normal", "Urgent"].map(p => <button key={p} onClick={() => setMessageForm(f => ({
            ...f,
            priority: p
          }))} style={{
            flex: 1,
            padding: "8px 0",
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            border: `1px solid ${messageForm.priority === p ? p === "Urgent" ? "#C62828" : NAVY : LIGHT}`,
            background: messageForm.priority === p ? p === "Urgent" ? "#FFEBEE" : PALE : "#FFFFFF",
            color: messageForm.priority === p ? p === "Urgent" ? "#C62828" : NAVY : "#607D8B"
          }}>
                  {p === "Urgent" ? "Urgent" : "Normal"}
                </button>)}
            </div>

            <div style={{
          fontSize: 11.5,
          color: "#90A4AE",
          marginTop: 16,
          lineHeight: 1.5
        }}>
              Messages are scoped to the currently selected hostel ({hostel}). Switch hostel at the top to message about the other hostel.
            </div>
          </div>
        </div>}
    </div>;
}
function StatCard({
  label,
  value,
  color,
  icon
}) {
  return <div style={{
    background: "#FFFFFF",
    borderRadius: 18,
    border: `1px solid ${LIGHT}`,
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
  }}>
      <div style={{
      width: 42,
      height: 42,
      borderRadius: 12,
      background: PALE,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 20,
      flexShrink: 0
    }}>{icon}</div>
      <div>
        <div style={{
        fontSize: 22,
        fontWeight: 800,
        color,
        lineHeight: 1
      }}>{value}</div>
        <div style={{
        fontSize: 11,
        color: "#607D8B",
        marginTop: 2,
        fontWeight: 600
      }}>{label}</div>
      </div>
    </div>;
}
function EmptyBox({
  text
}) {
  return <div style={{
    textAlign: "center",
    padding: 60,
    color: "#607D8B",
    background: "#FFFFFF",
    borderRadius: 20,
    border: `1px dashed ${LIGHT}`
  }}>
      {text}
    </div>;
}
const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: NAVY,
  letterSpacing: "0.5px",
  marginBottom: 6
};
const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 10,
  border: `1px solid ${LIGHT}`,
  fontSize: 13,
  color: "#1A1A1A",
  outline: "none",
  boxSizing: "border-box"
};
const thStyle = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 11,
  fontWeight: 700,
  color: NAVY,
  letterSpacing: "0.4px",
  whiteSpace: "nowrap"
};
const tdStyle = {
  padding: "10px 14px",
  verticalAlign: "middle"
};